import { Locale } from '@synergy/models';
import { createSelector } from 'reselect';
import { deDE, frFR, itIT } from '@mui/x-date-pickers/locales';
import { defaultLocale as fallbackLocale } from '@synergy/config';
import { getLocalizedString } from '@synergy/utils';
import { sharedMessagesDE, sharedMessagesDE_CH, sharedMessagesFR, sharedMessagesIT } from '@synergy/translations';

import { AppState } from '../../store/store';
import { ConfigurationState } from './configuration-reducer';
import { FooterLink } from '../models/footer-link';
import { FooterLinkDto } from '../models/dto/footer-link-dto';
import { mapAddress } from '../models/mapper/address-mapper';
import { default as messagesDE } from '../../translations/de.json';
import { default as messagesDE_CH } from '../../translations/de-ch.json';
import { default as messagesFR } from '../../translations/fr.json';
import { default as messagesIT } from '../../translations/it.json';

const messages: { [index: string]: Record<string, string> } = {
    de: { ...sharedMessagesDE, ...messagesDE },
    'de-ch': { ...sharedMessagesDE, ...sharedMessagesDE_CH, ...messagesDE, ...messagesDE_CH },
    fr: { ...sharedMessagesFR, ...messagesFR },
    'it-ch': { ...sharedMessagesIT, ...messagesIT },
};

export const configurationState = (state: AppState): ConfigurationState => state.configuration;

export const accountSelector = createSelector(configurationState, configuration => configuration.account);
export const accountsSelector = createSelector(configurationState, configuration => {
    return configuration.accounts?.map(account => ({ name: account.name, id: account.id }));
});

export const intlSelector = createSelector(configurationState, configuration => {
    const defaultLocale = configuration.currentLocale.replace('_', '-');
    const locale = configuration.user?.locale.replace('_', '-') ?? defaultLocale;
    const topLevelLocale = locale.split('-')[0];

    return {
        locale,
        defaultLocale,
        topLevelLocale,
        pickerLocale: locale === Locale.French ? frFR : locale === Locale.Italien ? itIT : deDE,
        messages: { ...messages[defaultLocale], ...messages[locale] },
    };
});

export const currencySelector = createSelector(configurationState, configuration => configuration.currency);

export const localesSelector = createSelector(configurationState, state => {
    const userLocale = state.user?.locale;
    const currentLocale = state.currentLocale;
    const locales = [...(userLocale ? [userLocale] : []), ...(currentLocale ? [currentLocale] : []), fallbackLocale];
    return locales;
});

export const imageCachePathsSelector = createSelector(
    configurationState,
    configuration => configuration.imageCachePaths
);

export const userSelector = createSelector(configurationState, configuration => {
    if (!configuration.user) {
        return null;
    }

    const name = configuration.user?.fullName.split(' ');
    return {
        ...configuration.user,
        firstName: name?.[0] ?? '',
        lastName: name?.[1],
    };
});
export const accountDeliveryAddressesSelector = createSelector(
    configurationState,
    configuration => configuration.account?.addresses?.filter(a => a.isDeliveryAddress)?.map(mapAddress) ?? []
);

export const headerSelector = createSelector(configurationState, configuration => {
    const header = configuration.header;

    return {
        activeProductCount: header?.activeProductCount || 0,
        ongoingDemandCount: header?.ongoingDemandCount || 0,
        pendingSalesOrderCount: header?.pendingSalesOrderCount || 0,
    };
});

export const footerSelector = createSelector([localesSelector, configurationState], (locales, state) => {
    const footer = state.footer;
    const localizeLinks = (link: FooterLinkDto): FooterLink => {
        return {
            title: getLocalizedString(locales, link.title),
            url: link.url,
            icon: link.icon,
            elementId: link.elementId,
        };
    };

    return (
        footer && {
            ...footer,
            socialLinks: footer.socialLinks.map<FooterLink>(localizeLinks),
            newsLinks: footer.newsLinks.map<FooterLink>(localizeLinks),
            serviceLinks: footer.serviceLinks.map<FooterLink>(localizeLinks),
            bottomLinks: footer.bottomLinks.map<FooterLink>(localizeLinks),
        }
    );
});

export const mediaBaseUrlSelector = createSelector(configurationState, configuration => {
    return {
        mediaBaseUrl: configuration.mediaBaseUrl,
    };
});

export const shopAdminsSelector = createSelector(configurationState, configuration => {
    return {
        shopAdmins: configuration.shopAdmins,
    };
});
